import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { mithralayaFull } from '../assets';
import "../css/auth.css";

const OTP_LENGTH = 6;

const isLocalDev =
  process.env.NODE_ENV === 'development' &&
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

// Strip +, spaces, dashes; remove leading 91 country code -> 10-digit number
const normalizePhone = (raw) => {
  let p = raw.replace(/[^\d]/g, '');
  if (p.startsWith('91') && p.length === 12) p = p.slice(2);
  else if (p.startsWith('0') && p.length === 11) p = p.slice(1);
  return p;
};

// ---------- 6-box OTP input ----------
const OtpBoxes = ({ value, onChange, invalid, autoFocus }) => {
  const inputsRef = useRef([]);
  const digits = value.split('').concat(Array(OTP_LENGTH).fill('')).slice(0, OTP_LENGTH);

  const setDigit = (i, d) => {
    const next = digits.slice();
    next[i] = d;
    onChange(next.join('').replace(/\s+$/, ''));
  };

  const handleChange = (i, e) => {
    const v = e.target.value.replace(/[^0-9]/g, '');
    if (!v) { setDigit(i, ''); return; }
    const chars = v.split('');
    const next = digits.slice();
    let idx = i;
    for (const c of chars) {
      if (idx >= OTP_LENGTH) break;
      next[idx] = c;
      idx += 1;
    }
    onChange(next.join('').replace(/\s+$/, ''));
    const focusIdx = Math.min(idx, OTP_LENGTH - 1);
    inputsRef.current[focusIdx]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) {
      inputsRef.current[i - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && i > 0) inputsRef.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < OTP_LENGTH - 1) inputsRef.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    const text = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, OTP_LENGTH);
    if (!text) return;
    e.preventDefault();
    onChange(text);
    inputsRef.current[Math.min(text.length, OTP_LENGTH - 1)]?.focus();
  };

  return (
    <div className={`otp-boxes ${invalid ? 'invalid' : ''}`} onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (inputsRef.current[i] = el)}
          inputMode="numeric"
          maxLength={1}
          value={d}
          autoFocus={autoFocus && i === 0}
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={`Digit ${i + 1} of ${OTP_LENGTH}`}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
        />
      ))}
    </div>
  );
};

const AuthPage = () => {
  const location = useLocation();
  // /auth?mode=signup opens straight on "Create account"
  const [mode, setMode] = useState(
    new URLSearchParams(location.search).get('mode') === 'signup' ? 'signup' : 'login'
  );
  const [form, setForm] = useState({ name: '', phone: '', otp: '' });
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [otpRequested, setOtpRequested] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  const { requestOtp, verifyOtpLogin, signupWithOtp, devLogin } = useAuth();
  const navigate = useNavigate();
  // where to go after login: accepts a path string or a location object
  const rawFrom = location.state?.from;
  const from = (typeof rawFrom === 'string' ? rawFrom : rawFrom?.pathname ? rawFrom.pathname + (rawFrom.search || '') : '') || '/';

  useEffect(() => {
    if (resendTimer <= 0) return;
    const t = setTimeout(() => setResendTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendTimer]);

  const validators = {
    name: (v) => (v.trim().length >= 2 ? '' : 'Enter your full name'),
    phone: (v) => (/^[6-9][0-9]{9}$/.test(normalizePhone(v)) ? '' : 'Enter a valid 10-digit mobile number'),
    otp: (v) => (v.length === OTP_LENGTH ? '' : 'Enter the 6-digit code'),
  };

  const fieldError = (name) => (touched[name] ? validators[name](form[name]) : '');
  const isValid = (names) => names.every((n) => validators[n](form[n]) === '');

  const triggerShake = () => {
    setShake(true);
    setTimeout(() => setShake(false), 400);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (error) setError('');
  };


  const handlePhoneChange = (e) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 10);
    setForm((prev) => ({ ...prev, phone: digitsOnly }));
    if (error) setError('');
  };

  const handleBlur = (e) => setTouched((prev) => ({ ...prev, [e.target.name]: true }));

  const markTouched = (names) =>
    setTouched((prev) => {
      const next = { ...prev };
      names.forEach((n) => { next[n] = true; });
      return next;
    });

  const requiredFields = mode === 'signup' ? ['name', 'phone'] : ['phone'];

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    markTouched(requiredFields);
    if (!isValid(requiredFields)) return triggerShake();
    setOtpLoading(true);
    setError('');
    try {
      await requestOtp(normalizePhone(form.phone));
      setOtpRequested(true);
      setResendTimer(60);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send OTP');
      triggerShake();
    } finally {
      setOtpLoading(false);
    }
  };

  const handleChangeNumber = () => {
    setOtpRequested(false);
    setResendTimer(0);
    setError('');
    setForm((prev) => ({ ...prev, otp: '' }));
    setTouched((prev) => ({ ...prev, otp: false }));
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    markTouched(['otp']);
    if (!isValid(['otp'])) return triggerShake();
    setLoading(true);
    setError('');
    try {
      const data = mode === 'signup'
        ? await signupWithOtp({ name: form.name.trim(), phone: normalizePhone(form.phone), otp: form.otp })
        : await verifyOtpLogin({ phone: normalizePhone(form.phone), otp: form.otp });
      navigate(data.isAdmin ? '/admin' : from, { replace: true });
    } catch (err) {
      // show the server's exact reason (wrong code, expired, too many tries…)
      setError(
        err.response?.data?.message ||
          err.response?.data?.errors?.[0]?.msg ||
          'Could not verify the code. Please try again.'
      );
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (next) => {
    setMode(next);
    setOtpRequested(false);
    setResendTimer(0);
    setError('');
    setTouched({});
    setForm({ name: '', phone: '', otp: '' });
  };

  const handleDevLogin = async (as) => {
    setLoading(true);
    setError('');
    try {
      const data = await devLogin(as);
      navigate(data.isAdmin ? '/admin' : from, { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Dev login is only available on localhost.');
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  const heading = otpRequested
    ? { title: 'Verify your number', sub: <>We sent a 6-digit code to <strong>+91 {form.phone}</strong>.</> }
    : mode === 'dev'
      ? { title: 'Development login', sub: 'Skip OTP on localhost. This tab is hidden outside local development.' }
      : mode === 'signup'
      ? { title: 'Create your account', sub: 'Just your name and mobile number — no password needed.' }
      : { title: 'Welcome back', sub: 'Log in with your mobile number to see your orders and wishlist.' };

  return (
    <div className="auth-page">
      <div className={`auth-card ${shake ? 'shake' : ''}`}>
        {/* ---------- brand panel ---------- */}
        <aside className="auth-brand">
          <Link to="/" className="auth-logo-link" aria-label="Mithralaya home">
            <img src={mithralayaFull} alt="Mithralaya — Women's & Kids Wear" className="auth-logo" />
          </Link>
          <p className="auth-brand-line">Sarees, kurtis and churidar sets for women, and everyday wear for kids.</p>
          <ul className="auth-perks">
            <li>Track every order and return</li>
            <li>Save your wishlist and addresses</li>
            <li>Cash on delivery and 7-day returns</li>
          </ul>
        </aside>

        {/* ---------- form panel ---------- */}
        <section className="auth-form-panel" aria-live="polite">
          {!otpRequested && (
            <div
              className={`auth-tabs${isLocalDev ? ' has-dev' : ''}`}
              role="tablist"
              aria-label="Log in or create an account"
            >
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'login'}
                className={mode === 'login' ? 'is-active' : ''}
                onClick={() => mode !== 'login' && switchMode('login')}
              >
                Log in
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mode === 'signup'}
                className={mode === 'signup' ? 'is-active' : ''}
                onClick={() => mode !== 'signup' && switchMode('signup')}
              >
                Create account
              </button>
              {isLocalDev && (
                <button
                  type="button"
                  role="tab"
                  aria-selected={mode === 'dev'}
                  className={mode === 'dev' ? 'is-active' : ''}
                  onClick={() => mode !== 'dev' && switchMode('dev')}
                >
                  Dev
                </button>
              )}
            </div>
          )}

          <header className="auth-heading">
            <h1>{heading.title}</h1>
            <p>{heading.sub}</p>
          </header>

          {mode === 'dev' && isLocalDev && !otpRequested ? (
            <div className="auth-form">
              {error && <div className="error-message" role="alert">{error}</div>}
              <button
                className="btn-primary"
                type="button"
                disabled={loading}
                onClick={() => handleDevLogin('admin')}
              >
                {loading && <span className="spinner" />}
                Continue as admin
              </button>
              <button
                className="btn-dev-secondary"
                type="button"
                disabled={loading}
                onClick={() => handleDevLogin('user')}
              >
                Continue as customer
              </button>
              <p className="auth-terms">Local development only. OTP is not sent.</p>
            </div>
          ) : !otpRequested ? (
            <form onSubmit={handleRequestOtp} noValidate className="auth-form">
              {mode === 'signup' && (
                <div className="auth-field">
                  <label htmlFor="name" className="auth-label">Full name</label>
                  <div className={`text-field ${fieldError('name') ? 'invalid' : ''}`}>
                    <input
                      id="name"
                      name="name"
                      autoFocus
                      placeholder="e.g. Priya Raman"
                      value={form.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="name"
                    />
                  </div>
                  {fieldError('name') && <span className="field-error-text">{fieldError('name')}</span>}
                </div>
              )}

              <div className="auth-field">
                <label htmlFor="phone" className="auth-label">Mobile number</label>
                <div className={`phone-field ${fieldError('phone') ? 'invalid' : ''}`}>
                  <span className="phone-prefix">+91</span>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    maxLength={10}
                    placeholder="10-digit mobile number"
                    autoFocus={mode === 'login'}
                    value={form.phone}
                    onChange={handlePhoneChange}
                    onBlur={handleBlur}
                    autoComplete="tel-national"
                  />
                </div>
                {fieldError('phone') && <span className="field-error-text">{fieldError('phone')}</span>}
              </div>

              {error && <div className="error-message" role="alert">{error}</div>}

              <button className="btn-primary" type="submit" disabled={otpLoading}>
                {otpLoading && <span className="spinner" />}
                {otpLoading ? 'Sending code…' : 'Send OTP'}
              </button>

              <p className="auth-terms">
                By continuing, you agree to our <Link to="/terms-conditions">Terms of Use</Link> and{' '}
                <Link to="/privacy-policy">Privacy Policy</Link>.
              </p>
            </form>
          ) : (
            <form onSubmit={handleVerify} noValidate className="auth-form">
              <OtpBoxes
                value={form.otp}
                onChange={(v) => { setForm((prev) => ({ ...prev, otp: v })); if (error) setError(''); }}
                invalid={!!fieldError('otp')}
                autoFocus
              />
              {fieldError('otp') && <span className="field-error-text center">{fieldError('otp')}</span>}

              {error && <div className="error-message center" role="alert">{error}</div>}

              <button className="btn-primary" type="submit" disabled={loading}>
                {loading && <span className="spinner" />}
                {loading ? 'Verifying…' : mode === 'signup' ? 'Verify and create account' : 'Verify and log in'}
              </button>

              <div className="auth-otp-actions">
                <button
                  type="button"
                  className="link-btn"
                  disabled={otpLoading || resendTimer > 0}
                  onClick={handleRequestOtp}
                >
                  {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend code'}
                </button>
                <button type="button" className="link-btn" onClick={handleChangeNumber}>
                  Change number
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    </div>
  );
};

export default AuthPage;
